<template>
  <RevCard>
    <RevAccordionList :has-external-borders="false">
      <ul>
        <li class="flex flex-row flex-wrap p-24">
          <RevIllustration
            v-if="product.images?.[0]"
            alt=""
            aria-hidden="true"
            class="mr-20 flex self-start"
            :height="80"
            :src="product.images[0]"
            :width="80"
          />

          <div class="mt-8 md:flex-1">
            <h2 class="body-1-bold">
              {{ product.title }}
            </h2>
            <div class="body-2-tabular">
              {{ i18n(translations.productId, { productId: product.id }) }}
            </div>
            <div class="body-2-tabular">
              {{ i18n(translations.bmId, { bmId: product.bmId }) }}
            </div>
            <FormattedMessage
              v-if="product.dualSim"
              class="body-2 flex items-center gap-4"
              :definition="translations.simType"
            >
              <template #simType>
                <span class="body-2-bold">{{ product.dualSim }}</span>
              </template>
            </FormattedMessage>
          </div>

          <div
            class="mt-16 flex w-full flex-col justify-center space-y-12 md:ml-3 md:mt-0 md:w-auto"
          >
            <RevButton
              size="small"
              :to="{
                name: ROUTE_NAMES.LISTINGS.CREATE,
                params: {
                  productId: product.id,
                },
              }"
              variant="primary"
              @click="handleSellItemClick"
            >
              {{ i18n(translations.actionSellItem) }}
            </RevButton>
            <RevButton
              size="small"
              :to="duplicateRoute"
              variant="secondary"
              @click="handleAddSimilarClick"
            >
              {{ i18n(translations.actionSellSimilarVersion) }}
            </RevButton>
          </div>
        </li>
      </ul>

      <RevAccordionItem class="px-24">
        <template #title>
          {{ i18n(translations.actionShowDetails) }}
        </template>
        <template #body>
          <ProductCardDetails
            :product-id="product.id"
            :without-header="false"
          />
        </template>
      </RevAccordionItem>
    </RevAccordionList>
  </RevCard>
</template>

<script setup lang="ts">
import { useRuntimeConfig } from '#imports'
import { computed } from 'vue'

import type { ProductSellerSearch } from '@backmarket/http-api/src/api-specs-catalog/catalog-access/products.types'
import FormattedMessage from '@backmarket/nuxt-module-i18n/FormattedMessage.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevAccordionItem } from '@ds/components/AccordionItem'
import { RevAccordionList } from '@ds/components/AccordionList'
import { RevButton } from '@ds/components/Button'
import { RevCard } from '@ds/components/Card'
import { RevIllustration } from '@ds/components/Illustration'

import { ROUTE_NAMES } from '~/constants/routes'

import translations from './ProductCard.translations'
import ProductCardDetails from './components/ProductCardDetails/ProductCardDetails.vue'

const props = defineProps<{
  product: ProductSellerSearch
  productPosition: number
}>()

const { FF_BO_SELLER_IS_NEW_LISTING_CREATE_PRODUCT_OPEN } =
  useRuntimeConfig().public
const i18n = useI18n()
const tracking = useTracking()

const duplicateRoute = computed(() => {
  const isNewListingCreateProductOpen =
    FF_BO_SELLER_IS_NEW_LISTING_CREATE_PRODUCT_OPEN

  if (isNewListingCreateProductOpen) {
    return {
      name: ROUTE_NAMES.LISTINGS.PRODUCT.CREATE,
      query: {
        productId: props.product.id,
      },
    }
  }

  return {
    name: ROUTE_NAMES.BADOOM.LISTINGS.PRODUCT.DUPLICATE,
    params: {
      productId: props.product.bmId,
    },
  }
})

function handleSellItemClick() {
  tracking.trackClick({
    page_type: 'listings_create_product_search',
    zone: 'product_clicked',
    name: 'sell_product',
    product_id: props.product.bmId,
    position: props.productPosition,
  })
}
function handleAddSimilarClick() {
  tracking.trackSellItem({
    page_type: 'listings_create_product_search',
    zone: 'product_clicked',
    name: 'listings_add_similar_item_clicked',
    product_id: props.product.bmId,
    position: props.productPosition,
  })
}
</script>
